/**
 * <AIAssistPanel /> — the 5-panel AI assistance display
 * (commandment 40, M30.5.2).
 *
 * Renders ONE of the 5 panels for an HR-initiated transaction
 * form. The panel set is locked:
 *   - validation       (error / warning / info findings)
 *   - cross_reference  (info findings sourced from other types)
 *   - suggestions      (proposed values + alternative dates)
 *   - completeness     (nudges for commonly-forgotten fields)
 *   - auto_fill        (canonical reason text + amount previews)
 *
 * Props-driven — the form passes the panel + the
 * AIPanelFindings[] payload + the transaction type (so the tier
 * badge resolves correctly). The component handles:
 *   - Panel-specific icon + heading
 *   - Sonnet/Haiku tier badge (from the M30 tier registry)
 *   - Per-finding row with severity-toned chip + message +
 *     citation + suggested value
 *   - "Accept" action for suggestions/auto-fill findings whose
 *     suggested_value is non-null — wired via onAcceptSuggestion
 *     prop so the form can apply the value to its state.
 *   - Empty state when no findings — keeps the panel visible so
 *     HR scans every panel consistently.
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Info,
  type LucideIcon,
  Sparkles,
  Wand,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type Panel,
  type TransactionType,
  resolveTier,
} from "../../lib/ai-tier-registry";

// ---------------------------------------------------------------------------
// Public types — match the backend AIPanelFindings / ValidationFinding
// shape but exposed as TS so forms can construct them without a fetch.
// ---------------------------------------------------------------------------

export type FindingSeverity = "error" | "warning" | "info";

export interface AssistFinding {
  panel: Panel;
  severity: FindingSeverity;
  /** Machine code — stable; used by ai_findings_acknowledged + tests. */
  code: string;
  /** Human-readable message; rendered to HR in the active locale. */
  message: string;
  field_ref?: string | null;
  /** When set + non-null, the panel renders an "Accept" button that
   *  calls onAcceptSuggestion(field_ref, suggested_value). */
  suggested_value?: unknown;
  citation?: string | null;
}

export interface AssistPanelFindings {
  panel: Panel;
  findings: AssistFinding[];
}

export interface AIAssistPanelProps {
  transactionType: TransactionType;
  /** The panel data shape, as returned by transaction_assist()
   *  with one entry per Panel. */
  panel: AssistPanelFindings;
  /** Optional handler for suggestion/auto-fill "Accept" clicks. */
  onAcceptSuggestion?: (
    fieldRef: string,
    suggestedValue: unknown,
  ) => void;
  /** Acknowledged finding codes — the form ships these in
   *  payload.ai_findings_acknowledged so the commit endpoint
   *  knows which errors HR dismissed. */
  acknowledgedCodes?: Set<string>;
  onAcknowledge?: (code: string) => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Panel chrome — icon + label + tone per panel variant
// ---------------------------------------------------------------------------

interface PanelChrome {
  icon: LucideIcon;
  titleKey: string;
  emptyKey: string;
  /** Tone for the panel's header strip — color-coded so HR
   *  parses the 5 panels at a glance. */
  toneClass: string;
}

const PANEL_CHROME: Record<Panel, PanelChrome> = {
  validation: {
    icon: AlertCircle,
    titleKey: "ai_panel.validation_title",
    emptyKey: "ai_panel.validation_empty",
    toneClass: "border-state-denied/30 bg-state-denied-bg/40",
  },
  cross_reference: {
    icon: Info,
    titleKey: "ai_panel.cross_reference_title",
    emptyKey: "ai_panel.cross_reference_empty",
    toneClass: "border-state-info/30 bg-state-info-bg/40",
  },
  suggestions: {
    icon: Sparkles,
    titleKey: "ai_panel.suggestions_title",
    emptyKey: "ai_panel.suggestions_empty",
    toneClass: "border-ai/30 bg-ai-bg/40",
  },
  completeness: {
    icon: CheckCircle,
    titleKey: "ai_panel.completeness_title",
    emptyKey: "ai_panel.completeness_empty",
    toneClass: "border-state-approved/30 bg-state-approved-bg/40",
  },
  auto_fill: {
    icon: Wand,
    titleKey: "ai_panel.auto_fill_title",
    emptyKey: "ai_panel.auto_fill_empty",
    toneClass: "border-brand-teal/30 bg-brand-teal/5",
  },
};

const SEVERITY_CHROME: Record<
  FindingSeverity,
  { icon: LucideIcon; toneClass: string }
> = {
  error: {
    icon: AlertCircle,
    toneClass: "text-state-denied",
  },
  warning: {
    icon: AlertTriangle,
    toneClass: "text-state-pending",
  },
  info: {
    icon: Info,
    toneClass: "text-state-info",
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIAssistPanel({
  transactionType,
  panel,
  onAcceptSuggestion,
  acknowledgedCodes,
  onAcknowledge,
  className,
}: AIAssistPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const chrome = PANEL_CHROME[panel.panel];
  const tier = resolveTier(transactionType, panel.panel);
  const PanelIcon = chrome.icon;

  return (
    <section
      className={[
        "rounded-lg border shadow-sm",
        chrome.toneClass,
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-labelledby={`ai-panel-${panel.panel}-title`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border-subtle/40 px-4 py-2">
        <div className="flex items-center gap-2">
          <PanelIcon
            aria-hidden
            className="h-4 w-4 text-text-primary"
          />
          <h3
            id={`ai-panel-${panel.panel}-title`}
            className="text-sm font-semibold text-text-primary"
          >
            {t(chrome.titleKey)}
          </h3>
          <TierBadge tier={tier} />
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="rounded p-1 text-text-secondary hover:bg-surface-sunken"
          aria-label={
            collapsed
              ? t("ai_panel.expand")
              : t("ai_panel.collapse")
          }
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </button>
      </header>

      {!collapsed && (
        <div className="flex flex-col divide-y divide-border-subtle/40">
          {panel.findings.length === 0 ? (
            <p className="px-4 py-3 text-sm text-text-secondary">
              {t(chrome.emptyKey)}
            </p>
          ) : (
            panel.findings.map((f) => (
              <FindingRow
                key={f.code}
                finding={f}
                onAcceptSuggestion={onAcceptSuggestion}
                acknowledged={
                  acknowledgedCodes?.has(f.code) ?? false
                }
                onAcknowledge={onAcknowledge}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TierBadge({ tier }: { tier: "sonnet" | "haiku" }) {
  const { t } = useTranslation();
  const isSonnet = tier === "sonnet";
  return (
    <span
      className={[
        "ms-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        isSonnet
          ? "bg-brand-purple/15 text-brand-purple"
          : "bg-brand-teal/15 text-brand-teal",
      ].join(" ")}
      title={t(
        isSonnet
          ? "ai_panel.tier_sonnet_tooltip"
          : "ai_panel.tier_haiku_tooltip",
      )}
    >
      <Sparkles aria-hidden className="h-3 w-3" />
      {t(isSonnet ? "ai_panel.tier_sonnet" : "ai_panel.tier_haiku")}
    </span>
  );
}

function FindingRow({
  finding,
  onAcceptSuggestion,
  acknowledged,
  onAcknowledge,
}: {
  finding: AssistFinding;
  onAcceptSuggestion?: (
    fieldRef: string,
    suggestedValue: unknown,
  ) => void;
  acknowledged: boolean;
  onAcknowledge?: (code: string) => void;
}) {
  const { t } = useTranslation();
  const sev = SEVERITY_CHROME[finding.severity];
  const SevIcon = sev.icon;
  const canAccept =
    finding.field_ref &&
    finding.suggested_value !== undefined &&
    finding.suggested_value !== null;
  const isBlockingError = finding.severity === "error";

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-start gap-2">
        <SevIcon
          aria-hidden
          className={["mt-0.5 h-4 w-4 shrink-0", sev.toneClass].join(
            " ",
          )}
        />
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-sm text-text-primary">{finding.message}</p>
          {finding.citation && (
            <p className="text-xs text-text-secondary">
              {finding.citation}
            </p>
          )}
        </div>
        {isBlockingError && onAcknowledge && (
          <button
            type="button"
            onClick={() => onAcknowledge(finding.code)}
            className={[
              "rounded border px-2 py-0.5 text-xs",
              acknowledged
                ? "border-state-approved bg-state-approved-bg/40 text-state-approved"
                : "border-border-subtle text-text-secondary hover:bg-surface-sunken",
            ].join(" ")}
          >
            {acknowledged
              ? t("ai_panel.acknowledged")
              : t("ai_panel.acknowledge")}
          </button>
        )}
      </div>
      {canAccept && (
        <div className="ms-6 flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              onAcceptSuggestion?.(
                finding.field_ref as string,
                finding.suggested_value,
              )
            }
            className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-brand-fg hover:bg-brand-hover"
          >
            <Wand aria-hidden className="h-3 w-3" />
            {t("ai_panel.accept_suggestion")}
          </button>
        </div>
      )}
    </div>
  );
}

export type AIAssistPanelChrome = PanelChrome;
